import React, { useState } from 'react';
import { Box, Container, Grid, Typography, useTheme, Chip, Button as MuiButton } from '@mui/material';
import TopUtilityBar from '@/components/sections/TopUtilityBar';
import HeaderNav from '@/components/sections/HeaderNav';
import FooterSection from '@/components/sections/FooterSection';
import RFQFormSection from '@/components/sections/RFQFormSection';
import Card from '@/components/ui/Card';

export const InsightsPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Façade Engineering', 'Glass Specs', 'Quality & Safety', 'Infrastructure Safety'];

  const articles = [
    {
      title: 'Selecting the Right Curtain Wall System: Unitized vs. Semi-Unitized',
      category: 'Façade Engineering',
      date: 'AUG 2024',
      readTime: '6 MIN READ',
      summary: 'A detailed engineering breakdown comparing unitized factory-assembled curtain walls against semi-unitized systems for high-rise commercial developments in India.',
      takeaway: 'Why in-house manufacturing control (7,000 sq.ft. Sarjapur yard) accelerates unitized panel installation by 40% on site.',
      image: 'https://images.unsplash.com/photo-1541888946425-d0fbb186a5b3?q=80&w=1000&auto=format&fit=crop',
    },
    {
      title: 'Understanding Glass Specs: DGU, Low-E, and Laminated Safety Glass',
      category: 'Glass Specs',
      date: 'JUL 2024',
      readTime: '5 MIN READ',
      summary: 'How solar heat gain coefficient (SHGC), U-values, and acoustic STC ratings impact HVAC energy efficiency in commercial office buildings.',
      takeaway: 'Optimizing acoustic insulation and solar control in high-density IT corridors like Whitefield & Outer Ring Road.',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1000&auto=format&fit=crop',
    },
    {
      title: 'Implementing a Zero-Harm Safety Culture on High-Rise Construction Sites',
      category: 'Quality & Safety',
      date: 'JUN 2024',
      readTime: '7 MIN READ',
      summary: 'Practical protocols for HIRA risk assessment, Permit-to-Work execution, and daily Toolbox Talks (TBT) during structural glazing installations.',
      takeaway: 'Enforcing ISO 45001 standards during at-height glass hoisting and structural steel erection.',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1000&auto=format&fit=crop',
    },
    {
      title: 'Thermoplastic vs. Water-Based Road Markings: Durability & IRC Compliance',
      category: 'Infrastructure Safety',
      date: 'MAY 2024',
      readTime: '4 MIN READ',
      summary: 'Why hot-applied retro-reflective thermoplastic paint with glass beads provides 3x longer service life for IT parks and metro corridors.',
      takeaway: 'Full alignment with IRC-35 and MoRTH technical specifications for long-term retro-reflectivity.',
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=1000&auto=format&fit=crop',
    },
  ];

  const filteredArticles = activeCategory === 'All'
    ? articles
    : articles.filter((a) => a.category === activeCategory);

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', color: theme.palette.text.primary, transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      <TopUtilityBar />
      <HeaderNav />

      {/* Hero Banner */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 1.5,
                backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.08)',
                border: `1px solid ${isDark ? 'rgba(0, 102, 255, 0.3)' : 'rgba(0, 80, 203, 0.2)'}`,
                mb: 3,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                auto_stories
              </span>
              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                KNOWLEDGE BASE & TECHNICAL INSIGHTS
              </Typography>
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', sm: '3.25rem', md: '3.75rem' },
                color: theme.palette.text.primary,
                fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Space Grotesk', sans-serif",
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: '0.01em',
                textTransform: 'uppercase',
                mb: 3,
              }}
            >
              CONSTRUCTION KNOWLEDGE &<br />
              <Box component="span" sx={{ color: theme.palette.primary.main }}>
                ENGINEERING INSIGHTS.
              </Box>
            </Typography>

            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.7, mb: 5 }}>
              Technical articles, case studies, quality standards, and industry insights curated by Inverika's engineering and pre-construction teams.
            </Typography>

            {/* Category Filter Chips */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {categories.map((cat) => (
                <MuiButton
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  variant={activeCategory === cat ? 'contained' : 'outlined'}
                  size="small"
                  sx={{
                    borderRadius: '20px',
                    px: 2.5,
                    py: 0.75,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: activeCategory === cat ? theme.palette.primary.main : 'transparent',
                    color: activeCategory === cat ? '#ffffff' : theme.palette.text.primary,
                    borderColor: activeCategory === cat ? theme.palette.primary.main : theme.palette.divider,
                  }}
                >
                  {cat}
                </MuiButton>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Articles Grid */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {filteredArticles.map((article, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 0,
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box sx={{ height: 220, width: '100%', overflow: 'hidden' }}>
                    <Box
                      component="img"
                      src={article.image}
                      alt={article.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>

                  <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexGrow: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip label={article.category} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.1)', color: theme.palette.primary.main }} />
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                          {article.date} • {article.readTime}
                        </Typography>
                      </Box>

                      <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'Space Grotesk', color: theme.palette.text.primary, mb: 2, lineHeight: 1.35 }}>
                        {article.title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.65, mb: 3 }}>
                        {article.summary}
                      </Typography>
                    </Box>

                    <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600, display: 'block' }}>
                        Key Takeaway: {article.takeaway}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* RFQ Form Section */}
      <RFQFormSection />

      {/* Footer */}
      <FooterSection />
    </Box>
  );
};

export default InsightsPage;
